import { CircularProgress, IconButton, styled } from '@material-ui/core';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { attributes, Collection, Events, Linked } from '@type-r/models';
import { useCollection, useIO, useLinked, useModel } from '@type-r/react';
import { asCurrency, asDate, Button, CollapsableSection, CsvDownloader, div, Flex, FormCheckbox, Input, PopupDialog, ReadOnly, SelectSortBy, TilesList, SearchInput, asTimelessDate, Loading, AppLink } from 'lqx-react';
import * as React from 'react';
import { useContext, useMemo } from 'react';
import { execUnmatchInvoicesForMultiplePaymnets, InvoiceInfo, InvoiceMatch, RecsInvoice, RemittanceAdvice } from 'src/app/pages/inmatch/api';
import { MatchLabel, RemittanceAdviceIcon, RowH2Bold } from '../../../../../../common/inmatch';
import { InvoiceRow, SendToButton, UnmatchConfirmationDialog, ConfirmationDialog, InvalidDialog, ListItem } from './invoicesList';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CsvMapping } from 'lqx-react/src';
import { DigitizedIcon, inblockIconStyles } from 'common/invoice-aging';
import { span } from 'lqx-react';
import { InblockIcon } from 'common/invoice-aging/iconography';
import { Alert } from '@material-ui/lab';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';

export const sortInvoicesOptions = {
    invoiceNumber: 'Invoice Number',
    invoiceDueDate: 'Invoice Due Date',
    invoicedAmount: 'Invoiced Amount',
    unreconciledAmount: 'Unreconciled Amount'
};

export const InvoicesState = attributes({
    sortBy : {
        name : "unreconciledAmount",
        isAsc : true,
    },
    filter: '',
});

export const MatchedInvoices = ({ matches, paymentUnreconciledAmount, paymentId, disabled, showExport, currency, invoices, showCustomer } : {
    matches : Collection<InvoiceMatch>,
    paymentUnreconciledAmount?: number
    paymentId?: string,
    disabled?: boolean,
    showExport? : boolean,
    currency : string,
    invoices? : Collection<RecsInvoice>,
    showCustomer? : boolean
}) => {
    const state = useModel( InvoicesState );
    
    const $blurred = useLinked<boolean>(undefined);

    const readOnly = useContext(ReadOnly) || disabled || (!Can(UserEntitlementCatalogs.BankEntryWrite) && !Can(UserEntitlementCatalogs.BankEntryConfirm));

    const selectedMatches = useCollection.subsetOf( matches );

    invoices = invoices ? invoices : new Collection<RecsInvoice>();

    const sorted = useMemo( () => {
        const { name, isAsc } = state.sortBy;
        return matches.filter(x => !!x.invoice && x.filter(state.filter))
            .sort( (a, b) => a.isValid() === b.isValid() ? RecsInvoice.comparator( name as any, isAsc )(a.invoice, b.invoice) : !a.isValid() ? -1 : 1 ) ;
    },
    [ matches.length, state['_changeToken'], invoices['_changeToken'] ] );

    const [writeOffTotal, setWriteOffTotal] = React.useState(0);
    const [discountTotal, setDiscountTotal] = React.useState(0);
    const [cashAppliedTotal, setCashAppliedTotal] = React.useState(0);

    React.useEffect(() => {
        setWriteOffTotal(sorted.reduce((total : number, inv) => total + inv.writeOff, 0));
        setDiscountTotal(sorted.reduce((total : number, inv) => total + inv.discountAmount, 0));
        setCashAppliedTotal(sorted.reduce((total : number, inv) => total + inv.appliedAmount, 0));
    }, [matches['_changeToken']]);

    const unmatchMultiple = async () => {
        const ids = selectedMatches.map(inv => ({ paymentId: inv.payment.id, invoiceId: inv.invoiceId }))
        await execUnmatchInvoicesForMultiplePaymnets(ids);

        Events.trigger( 'matches:unmatch', ids.map(x => x.invoiceId));
        selectedMatches.reset();
    };   

        const renderer = useMemo( () => {
            return MatchedInvoicesTile( readOnly, disabled, selectedMatches, showCustomer, $blurred);
        }, [readOnly, disabled, selectedMatches['_changeToken'], $blurred.value]);
    
    const inMatchRecommendationsMessage = Boolean(matches.some(i => i.originType === 'heuristic') && matches.length);

    return (
        <CollapsableSection
            title={`Matched ${sorted.length} Invoices`}
            info={
                <>
                    {/*<MatchesSummary matches={ matches } />*/}
                    {asCurrency( sorted.reduce( ( acc : number, x ) => acc + x.appliedAmount + x.discountAmount + x.writeOff, 0 ), currency )} {inMatchRecommendationsMessage ? <SpanStyled>InMatch Recommendations</SpanStyled> : null}
                </>
            } 
            right={
                disabled || (!Can(UserEntitlementCatalogs.BankEntryWrite) &&
                !Can(UserEntitlementCatalogs.BankEntryConfirm)) ? 
                    undefined : sorted.length  ?
                        <UnmatchButton
                            onClick={ unmatchMultiple }
                            disabled={!selectedMatches || !selectedMatches.length}
                            blockAction={selectedMatches.some(inv => !!inv.remittanceAdviceId)}
                        /> : undefined
            }>
            <Flex row>
                { readOnly ? 
                    undefined : sorted.length ?
                    <>
                        <FormCheckbox
                            disabled={readOnly || !sorted.length }
                            $checked={
                                Linked.value(
                                    sorted.length ? sorted.length === selectedMatches.length : false,
                                    x => x ? selectedMatches.set( sorted ) : selectedMatches.reset()
                                )
                            }
                        />
                        <SearchInput
                            placeholder="Filter by Invoice #, Invoice Due date, or Invoice amount or Additional Reference Identifier"
                            $value={ state.$.filter }
                        />
                    </> : undefined }

                <Flex grow />
                {sorted && sorted.length ? 
                    <InvoiceMatchHeader>
                        <HeaderLabel>Write off</HeaderLabel>
                        <HeaderLabel>Discount</HeaderLabel>
                        <HeaderLabel>Applied amount</HeaderLabel>
                    </InvoiceMatchHeader> : null }
                
                {sorted.length ? <SelectSortBy 
                        $attr={ state.sortBy.$.name }
                        $asc={ state.sortBy.$.isAsc }
                        options={ sortInvoicesOptions }
                    /> : null}
                

            { showExport && sorted && sorted.length ? 
                <CsvDownloader filename={`invoices_${matches.first().payment.id}`} mapping={ csvFields } data={ sorted }>
                    <IconButton aria-label="Download" size="small">
                        <GetAppIcon/>
                    </IconButton>
                </CsvDownloader> : undefined
            }
            </Flex>

            {/* {matches.length ? (
                <SearchInput
                    placeholder="Filter by Invoice #, or Invoice amount, or Invoice promise to pay date"
                    $value={ state.$.filter }
                />) : null
            } */}
            <TilesList
                items={ sorted }
                itemSize={ 42 }
                maxVisibleItemCount={ 10 }
                renderer={ renderer }
            />

            <Flex row>
                <Flex grow />
                {matches && matches.length ? 
                    <InvoiceMatchFooter>
                        <MatchAmount>Total</MatchAmount>
                        <MatchAmount title={asCurrency(writeOffTotal, currency)}>{asCurrency(writeOffTotal, currency)}</MatchAmount>
                        <MatchAmount title={asCurrency(discountTotal, currency)}>{asCurrency(discountTotal, currency)}</MatchAmount>
                        <MatchAmount title={asCurrency(cashAppliedTotal, currency)}>{asCurrency(cashAppliedTotal, currency)}</MatchAmount>
                    </InvoiceMatchFooter> : null }
            </Flex>
        </CollapsableSection>
    );
}

export const UnfundedInvoicesState = attributes({
    sortBy : {
        name : "invoiceNumber",
        isAsc : true,
    },
    filter: '',
});

export const sortUnfundedInvoicesOptions = {
    invoiceNumber: 'Invoice Number',
    appliedAmount: 'Applied Amount',
};

export const UnfundedInvoices = ({ matches, currency, remitanceAdviceId, disabled } : {
    matches : InvoiceMatch[]
    currency : string,
    remitanceAdviceId : number,
    disabled? : boolean,
}) => {
    const state = useModel(UnfundedInvoicesState);
    const remittanceAdvice = useModel(RemittanceAdvice);
    
    const isReady = useIO( async () => {
        remittanceAdvice.id = remitanceAdviceId as any;
        await remittanceAdvice.fetch();
    });
    
    const sorted = useMemo( () => {
        const { name, isAsc } = state.sortBy;
        return matches.filter(x => x.filterUnfunded(state.filter)).sort( (a, b) => InvoiceMatch.comparator( name as any, isAsc )(a, b) );
    },
    [ matches['_changeToken'], state['_changeToken'] ] );

    const [cashAppliedTotal, setCashAppliedTotal] = React.useState(0);

    React.useEffect(() => {
        setCashAppliedTotal(matches.reduce((total : number, inv) => total + inv.appliedAmount, 0));
    }, [matches['_changeToken']]);

    const renderer = useMemo( () => {
        return UnfundedMatchedInvoicesTile(currency, remittanceAdvice.matches, disabled);
    }, [ remittanceAdvice.matches, matches['_changeToken']]);

    return (
        isReady ? 
        <CollapsableSection
            title={`Unfunded Matched ${matches.length} Invoices`}
            info={
                <>
                    {asCurrency( matches.reduce( ( acc : number, x ) => acc + x.appliedAmount + x.discountAmount + x.writeOff, 0 ), currency )}
                </>
            }>
            
            { remittanceAdvice && remittanceAdvice.isMissingSupplierName ? 
                <Alert severity="warning">Supplier is missing or multiple supplier are available in the RA. 
                    <AppLink href={`/remittance-advices/unreconciled/${remitanceAdviceId}`}>Update Remittance Advice #{remitanceAdviceId} to reconcile</AppLink></Alert>
                : undefined 
            }
            <Flex row>
                { matches.length && !disabled ?
                    <>
                        <SearchInput
                            placeholder="Filter by Invoice #, Applied amount"
                            $value={ state.$.filter }
                        />
                    </> : undefined }

                <Flex grow />
                
                {matches && matches.length ? 
                    <InvoiceMatchHeader>
                        <HeaderLabel>Applied amount</HeaderLabel>
                    </InvoiceMatchHeader> : null }
            </Flex>
            <TilesList
                items={ sorted }
                itemSize={ 42 }
                maxVisibleItemCount={ 10 }
                renderer={ renderer }
            />
            <Flex row>
                <Flex grow />
                {matches && matches.length ? 
                    <>
                        <MatchAmount title={asCurrency(cashAppliedTotal, currency)}>{asCurrency(cashAppliedTotal, currency)}</MatchAmount>
                        <MatchAmount>Total</MatchAmount>
                    </> : null }
            </Flex>
        </CollapsableSection> : <Loading/>
    );
}

const MatchedInvoicesTile = (readOnly: boolean, disabled: boolean, selectedMatches: Collection<InvoiceMatch>, showCustomer: boolean
    , $blurred: Linked<boolean>
) => ({ data, style, index }) => {
    const match : InvoiceMatch = data.items[ index ];
    const { invoice } = match;

    return (
        <div style={style} key={ match.cid } >
        {
        invoice ?
            <Flex row>
                { readOnly ? null : <FormCheckbox $checked={ selectedMatches.$includes( match ) } /> }
                <InvoiceRow key={ match.cid }
                    icon={
                        <DigitizedIcon invoice={ invoice }>
                            <MatchLabel type={match.originType}/>
                        </DigitizedIcon>
                    }
                    invoice={invoice}
                    middle={
                        !match.hasPendingIO() ?
                            <MatchCashApplied match={match} disabled={disabled} onBlur={() => $blurred.set(!$blurred.value)} />
                        : null
                    }
                    showCustomer={ showCustomer }
                >      
                    <ListField><RowH2Bold>{invoice.additionalReferenceIdentifier}</RowH2Bold></ListField>                              
                    { readOnly ? null :
                    <>
                        { !match.hasPendingIO() ?
                            <RemoveButton onClick={() => match.destroy()} blockAction={match.remittanceAdviceId}/>
                        :
                            <CircularProgress size={20}/>
                        }
                    </>}
                </InvoiceRow>
            </Flex>
        : undefined
        }
        </div>
    );
};

const UnfundedMatchedInvoicesTile = (currency: string, raMatches : Collection<InvoiceInfo>, disabled) => ({ data, style, index }) => {
    const invoiceMatch : InvoiceMatch = data.items[ index ];
    const iconClasses = inblockIconStyles();
    const raMatch = raMatches.find(m => m.invoiceNumber === invoiceMatch.invoiceNumber)

    return (
        <div style={style} key={ invoiceMatch.cid } >
        {
            <Flex row>
                <ListItem
                    icon={
                        <Flex alignCenter>
                            { raMatch.id ? <InblockIcon src={'/assets/images/inblock_logo.png'} classes={iconClasses} /> : undefined }
                            <RemittanceAdviceIcon />
                        </Flex>
                    }
                    title={ <RowH2Bold>{invoiceMatch.invoiceNumber}</RowH2Bold> }
                    extensionBox={invoiceMatch.invoiceNumber}
                >
                    {
                        !invoiceMatch.hasPendingIO() ?
                            <MatchAmount>
                                <Input
                                    disabled={disabled}
                                    positive number
                                    $value={ invoiceMatch.$.appliedAmount }
                                />
                            </MatchAmount>
                        : null
                    }
                    <ListField><RowH2Bold>{ raMatch ? asTimelessDate(raMatch.dueDate) : undefined }</RowH2Bold></ListField>
                </ListItem>
            </Flex>
        }
        </div>
    );
};

const MatchCashApplied = ({match, disabled, onBlur} : {
    match: InvoiceMatch,
    disabled?: boolean,
    onBlur: () => void;
}) => {

    const onWriteOffFocusOut = () => {
        if (match.validationError && match.validationError.nested.writeOff !== undefined) {
            match.setWriteOffMaxValue();
        }
        onBlur();
    }

    const onDiscountFocusOut = () => {
        if (match.validationError && match.validationError.nested.discountAmount !== undefined) {
            match.setDiscountMaxValue();
        }
        onBlur();
    }

    return (
        <>
            <MatchAmount>
                <Input
                    aria-label='Write Off'
                    disabled={disabled}
                    positive number
                    $value={ match.$.writeOff }
                    onBlur={ onWriteOffFocusOut }
                    
                />
            </MatchAmount>
            <MatchAmount>
                <Input
                    aria-label='Discount'
                    disabled={disabled}
                    positive number
                    $value={ match.$.discountAmount }
                    onBlur={ onDiscountFocusOut }
                />
            </MatchAmount>
            <MatchAmount>
                <Input
                    aria-label='Applied Amount'
                    disabled={disabled}
                    number
                    $value={ match.$.appliedAmount }
                    onBlur={ onBlur }
                />
            </MatchAmount>
        </>
    );
};

export const ListField = styled('div')({
    paddingLeft: 8,
    paddingRight: 8,
    minWidth: 120,
    fontSize: 12,
    color: '#A0A0A0'
})

const MatchAmount = div({
    width: 110,
    maxWidth: 110,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 110
})

const HeaderLabel = div({
    width: 120,
    color: '#728A97',
    fontFamily: 'Montserrat',
    fontSize: 14,
    whiteSpace: 'nowrap',
    fontWeight: 500,
    alignSelf: 'center',
    letterSpacing: 'NaNpx',
    textAlign: 'center'
});

const InvoiceMatchHeader = div({
    marginRight: 75,
    display: 'flex',
    flexDirection: 'row',
});

export const InvoiceMatchFooter = div({
    fontWeight : 500,
    marginRight: 327,
    display: 'flex',
    flexDirection: 'row',
    textAlign: 'right'
});

const RemoveButton = ({ onClick, disabled = null, blockAction }) => {

    const $isOpen = useLinked( false );

    return (
    <>
    <IconButton aria-label="Remove" size="small"
        onClick={ $isOpen.true } disabled={ disabled }
    >
        <HighlightOffIcon />
    </IconButton>
    { $isOpen.value && blockAction ? <InvalidDialog $isOpen={$isOpen} action='unmatching' /> : undefined } 
    {$isOpen.value && !blockAction ? <UnmatchConfirmationDialog $isOpen={$isOpen} onClick={onClick} /> : null }
    </>)
}

export const UnmatchButton = ({ onClick, disabled, blockAction, onClose = undefined }) =>
    MatchUnmatchButton({onClick, disabled, action: 'Unmatch', blockAction, onClose});

export const MatchUnmatchButton = ({ onClick, disabled, action, blockAction, onClose }
        : {onClick: Function, disabled: boolean, action: 'Match' | 'Unmatch', blockAction: boolean, onClose?: Function}) => {
    const $isOpen = useLinked( false );
    const readOnly = useContext(ReadOnly);

    return (
        <>
        <Button
            onClick={e => {
                e.stopPropagation();
                $isOpen.set(true);
            }}
            disabled={disabled}
            hidden={ readOnly }
        >
            { action }
        </Button>

        { blockAction ? 
            <InvalidDialog $isOpen={$isOpen} action={action === 'Match' ? 'matching' : 'unmatching'} onClose={onClose} /> : undefined }

        { $isOpen.value && !blockAction
            ? <ConfirmationDialog $isOpen={$isOpen} onClick={onClick} action={action} />
            : null}
        </>
    );
};

const csvFields : CsvMapping<InvoiceMatch> = {
    "Invoice Number" : x => x.invoice.invoiceNumber,
    "Write offf" : x => x.writeOff,
    "Discount": x => x.discountAmount,
    "Applied Amount" : x => x.appliedAmount,
    "Invoice Due Date" : x => asDate(x.invoice.invoiceDueDate),
    "Additional Reference Identifier": x => x.invoice.additionalReferenceIdentifier
};

const SpanStyled = span({
    fontWeight: 'bold',
    color: '#007bff',
    marginLeft: '10px',
    fontStyle: 'italic'
});
